// The flow editor page (`#!/flows/:id`): a payout flow as ONE list of steps you build by
// tapping the phone. A full page, not a modal — the editor needs the whole screen, and the
// URL is shareable/refreshable.
//
// Page tabs: "Flow" is the steps themselves (list on the left, the selected step's
// parameters — or the palette — on the right; the phone's screen is a floating window
// opened from the run bar, so it can sit next to the step it feeds); "Log" is the run's
// output; "General" is everything about the flow as a record — name, note, enabled, teams;
// "History" is every save, with a way back to any of them; "Info" is the token reference.
// One Save button, in the tab row, writes both halves — and records a version.
//
// There are no phases. The document is a single list: the engine runs it start to finish
// for a payout, and — when the panel asks for a balance on its own, for the launch
// thresholds — runs the same list only as far as the first balance read, never reaching the
// commit (see flow_runner.run_balance).
//
// Above the canvas sits the run bar: it plays the document as it stands — unsaved edits
// included — on the phone the inspector is pointed at, whole (▶) or a node at a time (⏭),
// and marks each step as the engine reaches it. It plays the flow whole, commit included —
// a run really pays, so it is authored against a test account. Its log lives on the Log tab
// (`.fe__runlog`), where it has room to be read, and the run bar follows it there. The
// screen window re-reads the phone only on its Refresh button — through the run's own
// Appium session while one is open; nothing else may touch the screen then.
//
// Branch bodies (then/else, and a repeat's body) render as indented lanes under their step,
// so the shape of the flow is visible without a canvas engine. They start FOLDED — a loaded
// flow opens as an outline of its top-level steps, and a run unfolds whatever it walks into.
// Jumps (`goto`) pick their destination from a dropdown of the top-level steps; the arrows
// are only DRAWN, over an SVG overlay, so there is no connection-dragging to get wrong. A destination that has no
// label yet is given one when it is chosen.
import { esc } from '../ui/modal';
import { clockIconSvg } from '../core/format';
import { createFloatWindow } from '../ui/float-window';
import { t } from '../ui/lang';
import { can } from '../core/access';
import { navigate, routeToHash } from '../core/router';
import { OPS, CATEGORIES, QUEUES, DEPOSIT_FIELDS, DEPOSIT_FILTER_FIELDS, SHIFT_FORBIDDEN_OPS, VALUE_TOKENS, XPATH_TOKENS, TOKEN_GROUPS, AMOUNT_FORMATS, OUTCOMES, outcomeOptions, COMMIT_MSG_FIELDS, COMMIT_MSG_DEFAULTS, COMMIT_SWIPE_FIELDS, tokenDoc, opLabel, flowProblems, newNode, embedPayoutFlows, embedAutoFlows, FLOW_PICK_KIND, AUTO_OPS, isFlowRef, DEPOSIT_ONLY_OPS, PAYOUT_ONLY_OPS, WEB_ONLY_OPS, PHONE_ONLY_OPS, CRITICAL_OPS, COMMIT_OPS, USSD_OPS, USSD_PAY_DEFAULT, SMS_TIMEOUT_DEFAULT, normalizeLoadedFlow, createPayoutFlow, updatePayoutFlow, fetchPayoutFlows, fetchFlowScopes, setPayoutFlowTeams, fetchFlowHistory, restoreFlowVersion, fetchOperationRun, } from './flows-shared';
import { mountFlowInspector } from './flow-inspector';
import { mountFlowRunPanel, marksFromEvents, formatRunMs, stepLabel, nodeAtPath, varsAtEnd, branchLogLines, ussdReplies, } from './flow-run-panel';
import { downloadFlowFile, pickFlowFile } from './flow-json';
// How much of the lane a step took fits on the step's row; the rest lives in the Context tab.
const LANE_CHIP_MAX = 32;
// Mirrors flow_runner's `_TRACE_SECRET_TOKENS`: the tokens the engine deliberately keeps out
// of a trace. Listed in the table without a value, so a step that types a PIN does not read
// as a step that had none.
const SECRET_TOKENS = new Set(['PIN', 'PASSWORD', 'PASSWORD_LOGIN', 'PASSWORD_SECRET', 'LOGIN', 'CREDENTIAL']);
// "skip on fail" as a glyph: the step's own settings are hints next to the run's verdict, so
// they get an icon and a tooltip instead of a word that pushes the step's name off the row.
const skipOnFailSvg = '<svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 4l5 4-5 4"/><path d="M11 4v8"/></svg>';
// Run-status glyphs. Distinct SHAPES, not just colours: the locator-match dot lives in the
// same corner of the row, and two same-sized dots meaning different things is a puzzle.
const RUN_OK_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 13 4.5 4.5L19 7"/></svg>';
const RUN_ERR_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';
const RUN_AT_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5v14l11-7z"/></svg>';
/** Started, never finished — the run ended on top of it. Same square as the Stop button. */
const RUN_CUT_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="7" width="10" height="10" rx="1.5"/></svg>';
/** Ops that can end the payout themselves: their fail message is the reported sentence. */
const ENDS_PAYOUT = ['outcome', 'wait', 'verify_success'];
/** Resolve a `0/then/2` path to the array that holds the step and its index in it. */
function locate(root, path) {
    const parts = path.split('/');
    let list = root;
    for (let i = 0; i < parts.length; i += 2) {
        const idx = Number(parts[i]);
        if (!Number.isInteger(idx) || idx < 0 || idx >= list.length)
            return null;
        if (i + 1 >= parts.length)
            return { list, index: idx };
        const lane = parts[i + 1];
        const node = list[idx];
        if (!node[lane])
            node[lane] = [];
        list = node[lane];
    }
    return null;
}
function nodeAt(root, path) {
    const at = locate(root, path);
    return at ? at.list[at.index] ?? null : null;
}
/**
 * Turn the locator of ONE keypad key into a template for all of them.
 *
 * A `keypad` step taps the app's own digit keys one per character, so it needs a locator
 * with `{CHAR}` where the digit is. The author picks the "7" key on screen; this puts the
 * placeholder in its place, which is the step nobody guesses on their own.
 */
function keyTemplate(xpath) {
    return xpath.replace(/'(\d)'/, "'{CHAR}'");
}
/** A short, human name for a step in dropdowns and arrow labels. */
function stepTitle(n, index) {
    const target = n.xpath ?? n.value ?? n.target ?? '';
    const tail = target ? ` — ${target.slice(0, 40)}` : '';
    return `${index + 1}. ${opLabel(n.op)}${tail}`;
}
/** Unique, stable-ish label for a step that is about to become a jump destination. */
function ensureId(node, taken) {
    if (node.id)
        return node.id;
    let i = 1;
    let id = `${node.op}_${i}`;
    while (taken.has(id)) {
        i += 1;
        id = `${node.op}_${i}`;
    }
    node.id = id;
    taken.add(id);
    return id;
}
/** Route entry point: load the flow this URL names, then mount the editor on it. */
export async function renderFlowEditor(flowId) {
    const content = document.getElementById('content');
    document.getElementById('page-title').textContent = t('pf.flows');
    content.innerHTML = `<p class="settings-hint">${esc(t('common.loading'))}</p>`;
    let record;
    let scopes = { teams: [] };
    try {
        const [flows, sc] = await Promise.all([fetchPayoutFlows(), fetchFlowScopes()]);
        record = flows.find((f) => f.id === flowId);
        scopes = sc;
    }
    catch (e) {
        content.innerHTML = `<p class="settings-error">${esc(e.message)}</p>`;
        return;
    }
    if (!record) {
        content.innerHTML = `<p class="settings-hint">${esc(t('pf.flow_not_found'))}</p>`;
        return;
    }
    document.getElementById('page-title').textContent = record.name;
    mountEditor(content, record, scopes);
}
/**
 * The name a run's copy is offered under: the flow it came from plus when the copy was
 * taken. Flow names are unique, and "AstroPay Payout (copy)" collides with the next copy —
 * the stamp both avoids that and says which of them is which.
 */
function copyName(name) {
    const d = new Date();
    const two = (n) => String(n).padStart(2, '0');
    return `${name} ${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())} `
        + `${two(d.getHours())}:${two(d.getMinutes())}`;
}
/**
 * The stored run as the step list reads it: a RunHandle over marks that never change.
 *
 * The editor asks a run the same three questions whether it is happening now or happened
 * last Tuesday, so a finished run is just a handle whose answers are already known.
 *
 * Two layers, because ONE pass is reported in pieces: the rest of the pass (its other
 * operations and the tail nobody reported) lights the path this operation's own trace
 * cannot — it stops at its own report — and this operation's marks are laid over it, so a
 * step both walked is this run's verdict, never a neighbour's.
 */
function storedRunHandle(run) {
    const marks = marksFromEvents(run.trace, run.failed_at);
    const pass = marksFromEvents(run.pass_trace);
    return {
        step: (path) => {
            const own = marks.get(path);
            if (own)
                return own;
            const other = pass.get(path);
            return other ? { ...other, other: true } : null;
        },
        hasMarks: () => marks.size > 0 || pass.size > 0,
        refresh: () => { },
        say: () => { },
    };
}
/**
 * The stored run as log lines — one line per step, like the live log.
 *
 * The engine records a step twice (it started, it ended); the run bar rewrites the first
 * line into the second as it watches, and reading a finished run the same way means simply
 * dropping the "started" line of every step that got a verdict. What is left of a `▸` is
 * the step the run was still inside when it ended, which is exactly the one worth seeing.
 */
function runLogLines(run) {
    const trace = run.trace;
    // The step as the flow names it ("10 › then · Tap — «Enviar»"), not the bare path the
    // engine records: only the `at` event carries the op, so the name is read off the
    // document the trace belongs to — which is exactly the one this page is drawing.
    const label = (e) => stepLabel(e.path ?? '', e.op ?? nodeAtPath(run.doc.steps ?? [], e.path ?? '')?.op ?? '', nodeAtPath(run.doc.steps ?? [], e.path ?? ''));
    // A container closes long after the steps it holds, so its verdict is looked for
    // through the whole tail — up to the next time the same step starts, which is a
    // second visit (a loop) and settles nothing about this one.
    const settled = (i) => {
        const path = trace[i].path;
        for (let j = i + 1; j < trace.length; j += 1) {
            const e = trace[j];
            if (e.path !== path)
                continue;
            if (e.e === 'ok' || e.e === 'err')
                return true;
            if (e.e === 'at')
                return false;
        }
        return false;
    };
    const lines = [];
    trace.forEach((ev, i) => {
        if (ev.e === 'gap') {
            lines.push(`… ${t('pf.run_gap', { n: String(ev.dropped) })}`);
            return;
        }
        const e = ev;
        if (e.e === 'at' && !settled(i))
            lines.push(`▸ ${label(e)}`);
        else if (e.e === 'ok') {
            lines.push(`✓ ${label(e)}${e.value ? ` = ${e.value}` : ''}`
                + `${typeof e.ms === 'number' ? ` — ${formatRunMs(e.ms)}` : ''}`);
        }
        else if (e.e === 'err')
            lines.push(`✕ ${label(e)} — ${e.error ?? ''}`);
        else if (e.e === 'branch')
            lines.push(...branchLogLines(e));
    });
    return lines;
}
/**
 * Route entry point for `#!/flows/op/<operation>`: one operation's run, drawn on the flow
 * that produced it.
 *
 * The document is the one the SERVER hands back with the trace — the redaction that ran,
 * not today's — because a step path is a position in a document. The editor is the same
 * one, in view mode: the trajectory belongs on the steps it walked, and a second component
 * drawing the same step list its own way would have drifted from this one immediately.
 *
 * One document deep only: a step that PLAYS another flow still shows that flow as it stands
 * today (playedSteps reads the current list), so marks inside a played flow are placed on
 * its current steps. The run's own document is the one the operation was launched with, and
 * that is the one this page is about.
 */
export async function renderOperationRunPage(opId) {
    await renderStoredRunPage(() => fetchOperationRun(opId));
}
async function renderStoredRunPage(load) {
    const content = document.getElementById('content');
    document.getElementById('page-title').textContent = t('pf.flows');
    content.innerHTML = `<p class="settings-hint">${esc(t('common.loading'))}</p>`;
    let run;
    try {
        run = await load();
    }
    catch (e) {
        content.innerHTML = `<p class="settings-error">${esc(e.message)}</p>`;
        return;
    }
    // A flow deleted since the run still has its document in the trace's version, so the
    // view stands on what the run knew rather than refusing to open.
    const record = {
        id: run.flow?.id ?? run.flow_id ?? 0,
        task_type: run.flow?.task_type ?? run.doc.task_type ?? 'payout',
        app: run.flow?.app ?? run.doc.app ?? run.op.app_name,
        name: run.flow?.name ?? run.op.flow_name,
        note: '', is_global: false, enabled: true, default_pick: false, flow: run.doc,
        updated_by: null, updated_at: run.version_saved_at ?? run.op.created_at, team_ids: [],
    };
    document.getElementById('page-title').textContent = record.name;
    mountEditor(content, record, { teams: [] }, run);
}
function mountEditor(content, record, scopes, view, draft = false) {
    // Three modes, one editor. Viewing a stored run is read-only (it is someone else's
    // redaction of a flow, and evidence besides); `draft` is that same run opened for
    // editing, which is a NEW flow that does not exist until Save; everything else is the
    // ordinary editor. In both run modes the marks come from the trace, not a live session.
    const viewOnly = !!view && !draft;
    // Work on a copy: leaving without saving must leave the stored flow untouched.
    // Reassignable because importing a file swaps the whole document (see .fe-import).
    let doc = JSON.parse(JSON.stringify(record.flow ?? {}));
    doc.app = doc.app || record.app;
    if (!Array.isArray(doc.steps))
        doc.steps = [];
    normalizeLoadedFlow(doc);
    // The flow's own properties live on this page too — they belong to the flow, not to a
    // row in a list. Edited on the General tab, saved by the same Save button.
    // No "global" switch: a flow reaches the teams it is assigned to, full stop. Saving
    // normalizes the old global bit away, so what the picker shows is what applies.
    const general = {
        name: record.name,
        note: record.note,
        enabled: record.enabled,
        defaultPick: !!record.default_pick,
        teamIds: [...record.team_ids],
    };
    // Folded branch steps, held by node IDENTITY (not path) so a fold survives moving,
    // deleting or inserting steps around it — a path would point at a different node.
    const folded = new WeakSet();
    // The flow a `run_deposit_flow` / `run_payout_flow` step plays, opened for READING: it
    // is twenty-odd steps of another document, so it starts closed and is never editable
    // here — this is where the flow is, not what it is. A run walks into it (the engine
    // stamps its steps `<path>/flow/<i>`), so the marks land on these rows and the pass can
    // be watched whole, played flows inside played flows included.
    const playedOpen = new WeakSet();
    // Played flows whose own branches have been folded once (see the fold handler).
    const playedFolded = new WeakSet();
    // A fold the AUTHOR made, in either direction. `revealRun` opens whatever the run walked
    // on every repaint, so without this a branch the run is inside springs back open the
    // instant it is folded — the reveal is a default, the hand always wins.
    const byHand = new WeakSet();
    /**
     * Open the flow as an outline: every branch step folded.
     *
     * A real payout flow is thirty-odd steps with lanes nested inside lanes; expanded, the
     * top level alone does not fit on a screen and the list is unreadable. Only the
     * document as LOADED is folded — a branch you add yourself opens, because you are about
     * to fill it.
     */
    function foldBranches(list) {
        for (const n of list) {
            const branches = OPS[n.op]?.branches ?? [];
            if (!branches.length)
                continue;
            folded.add(n);
            for (const lane of branches)
                if (n[lane])
                    foldBranches(n[lane]);
        }
    }
    foldBranches(doc.steps);
    // Where the palette adds (see openPalette) — view state that has to outlive the
    // pane's re-render.
    let addTarget = null;
    /** Which group of the step's parameters is on screen — kept across re-renders. */
    let paramTab = 'step';
    let selectedPath = null;
    let sidePane = 'params';
    let tab = 'flow';
    let inspector = null;
    // A stored run answers from the trace; a live one is mounted with the run bar below.
    let runner = view ? storedRunHandle(view) : null;
    /** The step the run was on when the list was last drawn (see renderSteps). */
    let followedPath = null;
    let dirty = false;
    // The payout flows a deposit flow's `run_payout_flow` steps may play: this app's, offered by
    // name in the step's dropdown and embedded into the document a test run is given
    // (embedPayoutFlows). Fetched once; a payout flow has no use for them.
    let payoutFlows = [];
    // …and, for an auto flow, this app's deposit flows too: its composition node names one of
    // each kind, and `default` means a different flow in each list.
    let depositFlows = [];
    // The registry is superadmin's (GET /api/clicker/payout-flows). An admin reading a run
    // may not ask for it, so the dropdown/label falls back to the bare `#id` — the played
    // flow itself is embedded in the run and still renders under the step that played it.
    const loadPickable = (taskType, into) => {
        if (!can('page.flows'))
            return;
        void fetchPayoutFlows({ app: record.app, taskType })
            .then((f) => { into(f.filter((x) => x.id !== record.id)); render(); })
            .catch(() => { });
    };
    if (record.task_type === 'deposit' || record.task_type === 'auto') {
        loadPickable('payout', (f) => { payoutFlows = f; });
    }
    if (record.task_type === 'auto')
        loadPickable('deposit', (f) => { depositFlows = f; });
    /** What a pick reads as: the flow's name, or its bare id when the list has no such flow. */
    const flowPickName = (ref, from = payoutFlows) => from.find((f) => f.id === Number(ref))?.name ?? (ref ? `#${String(ref)}` : '');
    const queueLabel = (q) => t(`pf.queue_${String(q ?? '')}`);
    /** A step's flow dropdown: the flows of that kind, plus a pin the list no longer holds. */
    const flowOptions = (ref, from) => {
        const known = from.some((x) => x.id === Number(ref));
        return `<option value=""${!ref ? ' selected' : ''}>${esc(t('pf.pick_payout_flow'))}</option>`
            + from.map((x) => `<option value="${x.id}"${Number(ref) === x.id ? ' selected' : ''}>${esc(x.name)}${x.enabled ? '' : ` (${esc(t('pf.draft'))})`}</option>`).join('')
            + (ref && !known ? `<option value="${esc(String(ref))}" selected>#${esc(String(ref))}</option>` : '');
    };
    // A flow never sees another kind's steps in its palette or type list. An auto flow sees
    // only its own two: the deposit pass and the queues it drains after it.
    const auto = record.task_type === 'auto';
    const shift = record.task_type === 'shift';
    const categories = CATEGORIES.filter((c) => (auto ? c === 'auto' || c === 'task' || c === 'screen'
        // A «close shift» flow films the history: no money, no platform tasks.
        : shift ? c !== 'auto' && c !== 'task'
            : c !== 'auto' && c !== 'shift'));
    // …and inside those, each kind offers only its own ops: an auto flow the few it composes
    // the pass from, a deposit flow everything but the payout flow's own tasks request, a
    // payout flow everything but the deposit steps.
    // The palette also respects the flow's platform: a web flow hides phone ops and a phone
    // flow hides web ops (mirrors the engine's platform split).
    const web = doc.platform === 'web';
    const offersOp = (op) => {
        if (web && PHONE_ONLY_OPS.includes(op))
            return false;
        if (!web && WEB_ONLY_OPS.includes(op))
            return false;
        return auto ? AUTO_OPS.includes(op)
            : shift ? !SHIFT_FORBIDDEN_OPS.includes(op)
                : record.task_type === 'deposit' ? !PAYOUT_ONLY_OPS.includes(op)
                    : !DEPOSIT_ONLY_OPS.includes(op);
    };
    content.innerHTML = `
        <div class="farm-devs-header">
            <button class="back-btn" id="back-btn">← ${esc(t('pf.flows'))}</button>
            <h2 class="settings-title fe-title">${esc(record.name)}</h2>
        </div>
        <div class="fe fe--page">
            <div class="fe__tabs" role="tablist">
                <button type="button" class="fe-tab active" data-tab="flow">${esc(t('pf.tab_flow'))}</button>
                <button type="button" class="fe-tab" data-tab="log">${esc(t('pf.tab_log'))}</button>
                <button type="button" class="fe-tab" data-tab="general">${esc(t('pf.tab_general'))}</button>
                <button type="button" class="fe-tab" data-tab="history">${esc(t('pf.tab_history'))}</button>
                <button type="button" class="fe-tab" data-tab="info">${esc(t('pf.tab_info'))}</button>
                <span class="fe__saved"></span>
                <button type="button" class="modal__submit fe-save">${esc(t('pf.save_flow'))}</button>
            </div>
            <div class="fe__problems" style="display:none"></div>
            <div class="fe__run"></div>
            <div class="fe__body" data-tab="flow">
                <div class="fe__canvas">
                    <svg class="fe-arrows" aria-hidden="true"></svg>
                    <div class="fe-steps"></div>
                    <button type="button" class="fe-add" data-add="">+ ${esc(t('pf.add_step'))}</button>
                </div>
                <div class="fe__side">
                    <div class="fe-pane fe-pane--params"></div>
                    <div class="fe-pane fe-pane--palette" style="display:none"></div>
                </div>
            </div>
            <div class="fe__runlog" data-tab="log" style="display:none">
                <p class="settings-hint fe__runlog-empty">${esc(t('pf.log_empty'))}</p>
            </div>
            <div class="fe__general" data-tab="general" style="display:none"></div>
            <div class="fe__history" data-tab="history" style="display:none"></div>
            <div class="fe__info" data-tab="info" style="display:none"></div>
            <div class="settings-error modal__error fe-error" style="display:none"></div>
        </div>
    `;
    const root = content.querySelector('.fe');
    const stepsEl = root.querySelector('.fe-steps');
    const arrowsEl = root.querySelector('.fe-arrows');
    const paramsEl = root.querySelector('.fe-pane--params');
    const paletteEl = root.querySelector('.fe-pane--palette');
    const bodyEl = root.querySelector('.fe__body');
    const generalEl = root.querySelector('.fe__general');
    const historyEl = root.querySelector('.fe__history');
    const infoEl = root.querySelector('.fe__info');
    const problemsEl = root.querySelector('.fe__problems');
    const errEl = root.querySelector('.fe-error');
    const savedEl = root.querySelector('.fe__saved');
    const canvasEl = root.querySelector('.fe__canvas');
    const runEl = root.querySelector('.fe__run');
    const runLogEl = root.querySelector('.fe__runlog');
    const titleEl = content.querySelector('.fe-title');
    const steps = () => doc.steps;
    const markDirty = () => { dirty = true; savedEl.textContent = t('pf.unsaved'); };
    if (draft) {
        general.name = copyName(record.name);
        titleEl.textContent = general.name;
    }
    if (view) {
        if (draft)
            mountDraftView(view);
        else
            mountRunView(view);
    }
    /**
     * Turn the page into the read-only view of one stored run.
     *
     * Everything that WRITES goes: Save, the palette's way in, the row buttons (renderNode's
     * own readOnly). What is left is the flow as it ran, with a banner saying which operation
     * this is and whether the document under the marks is still the one that ran.
     */
    function mountRunView(run) {
        root.classList.add('fe--view');
        root.querySelector('.fe-save').remove();
        root.querySelector('.fe__canvas > .fe-add').remove();
        // The flow's own properties and its version history are the flow's, not this run's:
        // both offer edits (rename, restore) that this page is not the place for.
        for (const tab of ['general', 'history']) {
            root.querySelector(`.fe-tab[data-tab="${tab}"]`)?.remove();
        }
        // Which phone, which flow, which operation — the three facts a trajectory is read
        // against, one per line and label-aligned rather than run together: they are read,
        // not skimmed. The device is the one the operation was reported from, the flow is
        // named as the RUN knew it, not as the registry names it today.
        const device = [run.op.farm_name, run.op.device_name].filter(Boolean).join('/');
        const flow = [run.flow?.app || run.op.app_name, run.flow?.name || run.op.flow_name]
            .filter(Boolean).join('/');
        const fact = (label, value) => value
            ? `<div class="fe__runview-fact"><dt>${esc(label)}</dt><dd>${esc(value)}</dd></div>` : '';
        const chips = [
            run.stale
                ? `<span class="fe-chip fe-chip--warn">${esc(t('pf.run_view_stale'))}</span>`
                : `<span class="fe-chip">${esc(t('pf.run_view_version', {
                    at: new Date(run.version_saved_at ?? run.op.created_at).toLocaleString(),
                }))}</span>`,
            run.flow && !run.flow.exists
                ? `<span class="fe-chip fe-chip--warn">${esc(t('pf.run_view_flow_deleted'))}</span>` : '',
        ].join('');
        const banner = document.createElement('div');
        banner.className = 'fe__runview';
        banner.innerHTML = `
            <div class="fe__runview-head">
                <span class="fe__runview-title">${esc(t('pf.run_view_title', { status: run.op.status || '—' }))}</span>
                ${chips}
                ${can('page.flows') ? `<button type="button" class="modal__cancel fe__runview-edit">${esc(t('pf.run_view_edit'))}</button>` : ''}
            </div>
            <dl class="fe__runview-facts">
                ${fact(t('pf.run_view_flow'), flow)}
                ${fact(t('pf.run_view_device'), `${device || run.op.udid} (${run.op.udid})`)}
                ${fact(t('pf.run_view_op'), [run.op.tx_id, run.op.task_type,
            new Date(run.op.created_at).toLocaleString()].filter(Boolean).join(' · '))}
                ${fact(t('clickertab.comment'), run.op.comment)}
                ${passFact(run)}
            </dl>
        `;
        problemsEl.insertAdjacentElement('beforebegin', banner);
        // Editing a recorded run means editing a COPY of it: the document that ran is
        // evidence, and the flow it came from has moved on. The copy is not written
        // anywhere until Save — see the save button, which creates rather than updates.
        // …and only an author is offered it: the copy is saved as a NEW flow, which is
        // superadmin (POST /api/clicker/payout-flows). An admin reading a run has no button.
        banner.querySelector('.fe__runview-edit')?.addEventListener('click', () => {
            mountEditor(content, record, scopes, run, true);
        });
        // The log tab is the trace read as a story — the same lines the run bar writes while
        // it watches a live run, only all at once.
        const log = document.createElement('div');
        log.className = 'fr__log';
        log.innerHTML = runLogLines(run).map((line) => `<div>${esc(line)}</div>`).join('');
        runLogEl.appendChild(log);
    }
    /**
     * The other operations of the same clicker pass, as links.
     *
     * A trace stops where its operation was reported, so the steps before the first report
     * and after the last one live in the pass's neighbours — this row is how you walk to
     * them instead of wondering why the flow is only half lit.
     */
    function passFact(run) {
        if (run.pass.length < 2)
            return '';
        const links = run.pass.map((p, i) => {
            const cur = p.id === run.op.id;
            const title = [p.tx_id, p.task_type, p.status,
                new Date(p.created_at).toLocaleString()].filter(Boolean).join(' · ');
            const cls = `fe-chip fe__runview-pass${cur ? ' is-current' : ''}${p.failed ? ' fe-chip--warn' : ''}`;
            const label = `${i + 1}. ${p.tx_id || p.id}`;
            return cur
                ? `<span class="${cls}" title="${esc(title)}">${esc(label)}</span>`
                : `<a class="${cls}" href="${esc(routeToHash({ page: 'flows', opId: p.id }))}" title="${esc(title)}">${esc(label)}</a>`;
        }).join('');
        return `<div class="fe__runview-fact"><dt>${esc(t('pf.run_view_pass'))}</dt><dd>${links}</dd></div>`;
    }
    /**
     * The banner of a run opened for editing: this is a DRAFT, and Save creates a flow.
     *
     * The marks stay on the steps — the whole point of editing from here is fixing the step
     * the run broke on, and losing the trajectory the moment you touch the document would
     * take the reason for the edit off the screen.
     */
    function mountDraftView(run) {
        const banner = document.createElement('div');
        banner.className = 'fe__runview fe__runview--draft';
        banner.innerHTML = `
            <div class="fe__runview-head">
                <span class="fe__runview-title">${esc(t('pf.run_draft_title'))}</span>
                <span class="settings-hint">${esc(t('pf.run_draft_hint'))}</span>
            </div>
        `;
        problemsEl.insertAdjacentElement('beforebegin', banner);
        const log = document.createElement('div');
        log.className = 'fr__log';
        log.innerHTML = runLogLines(run).map((line) => `<div>${esc(line)}</div>`).join('');
        runLogEl.appendChild(log);
    }
    // ── rendering ──────────────────────────────────────────────────────────────
    /** The run's verdict on a step: running now, done (with how long it took), or failed. */
    /** A recorded response read as JSON where it is JSON, and verbatim where it is not —
     *  the engine caps it, so a truncated body simply stays one line. */
    function prettyJson(text) {
        try {
            return JSON.stringify(JSON.parse(text), null, 2);
        }
        catch {
            return text;
        }
    }
    /**
     * The placeholder table as it stood at this step, for the run being viewed.
     *
     * Empty string when there is nothing to show — no run, or a step the run never reached
     * — and the tab is then not offered at all (the group list drops empty groups).
     */
    function tokensHtml(path) {
        const step = path ? runner?.step(path) : null;
        const own = step?.vars ?? null;
        // Below where the run stopped there is no snapshot of the step's own, so the table
        // is the run's last one — offered on every step, said plainly to be the end state.
        const vars = own ?? (view ? varsAtEnd(view.trace) : null);
        const names = Object.keys(vars ?? {}).sort();
        // Which way the step went, first row: for a `for_each` that is the app's own line of
        // text, too long to live on the step itself — and one line per row it walked, since
        // the step's own chip can only show the last of them.
        const lanes = step?.lanes?.length
            ? step.lanes
            : [step?.laneText || (step?.lane ? t(`pf.lane_${step.lane}`) : '')].filter(Boolean);
        // A token the step itself spells but the trace has no value for — {PASSWORD}, {PIN}:
        // credentials are deliberately never recorded, and a table that simply omits them
        // reads as "the run never had one".
        const node = path ? nodeAtView(path) : null;
        const used = new Set();
        if (node) {
            for (const v of Object.values(node)) {
                if (typeof v !== 'string')
                    continue;
                for (const m of v.matchAll(/\{([A-Z_][A-Z0-9_]*)\}/g))
                    used.add(m[1]);
            }
        }
        const secret = [...used].filter((n) => SECRET_TOKENS.has(n) && !names.includes(n)).sort();
        // What the platform answered the step's own request with (take_tasks / take_deposits).
        // "No tasks" is a statement about the SERVER — this is the only place it can be read
        // without the clicker's log.
        // A USSD step's resp is the dialogs the network showed, one block each; an empty
        // list is the timeout case — "nothing ever appeared" is the answer, not a blank.
        const replies = ussdReplies(step?.resp);
        const resp = replies
            ? `<div class="fe-tokens-resp">
                <div class="fe-tokens-resp__head">${esc(t('pf.tokens_ussd'))}</div>
                ${replies.length
                ? replies.map((r) => `<pre class="fe-tokens-resp__body">${esc(r)}</pre>`).join('')
                : `<p class="settings-hint">${esc(t('pf.ussd_no_dialog'))}</p>`}
            </div>`
            : step?.resp
                ? `<div class="fe-tokens-resp">
                <div class="fe-tokens-resp__head">${esc(t('pf.tokens_resp'))}</div>
                <pre class="fe-tokens-resp__body">${esc(prettyJson(step.resp))}</pre>
            </div>`
                : '';
        if (!names.length && !lanes.length && !secret.length && !resp)
            return '';
        return `${resp}<dl class="fe-tokens">${lanes.map((lane, i) => `
            <div class="fe-tokens__row">
                <dt>${i ? '' : esc(t('pf.run_lane_row'))}</dt><dd>${esc(lane)}</dd>
            </div>`).join('')}${names.map((name) => `
            <div class="fe-tokens__row">
                <dt>{${esc(name)}}</dt><dd>${esc(vars[name])}</dd>
            </div>`).join('')}${secret.map((name) => `
            <div class="fe-tokens__row fe-tokens__row--secret">
                <dt>{${esc(name)}}</dt><dd>${esc(t('pf.tokens_secret'))}</dd>
            </div>`).join('')}</dl>
            <p class="settings-hint">${esc(t(own ? 'pf.tokens_run_hint' : 'pf.tokens_end_hint'))}</p>`;
    }
    function runChip(run, container = false) {
        if (!run)
            return '';
        // Every step the run was standing inside when it ended is marked `cut`, so a branch
        // wears the square only because one of its children does — noise on the row that
        // says nothing. The step it actually stopped on keeps it.
        if (run.mark === 'cut' && container)
            return `${laneChip(run)}`;
        const glyph = run.mark === 'ok' ? RUN_OK_SVG
            : run.mark === 'err' ? RUN_ERR_SVG
                : run.mark === 'cut' ? RUN_CUT_SVG : RUN_AT_SVG;
        const text = run.mark === 'ok' && run.ms !== undefined ? formatRunMs(run.ms) : '';
        const verdict = run.mark === 'err' && run.error ? run.error : t(`pf.run_m_${run.mark}`);
        // A mark the pass's OTHER operations left says so in the tooltip: dimmer is enough
        // to see it is not this operation's, not enough to say what it is.
        // A repeat's own sentence about how it ended ("until matched before pass 1/3") rides in
        // the verdict's tooltip: as a chip it was a line of prose on a row that already says
        // the step is done.
        const ended = !run.lane && run.laneText ? ` — ${run.laneText}` : '';
        const title = (run.other ? `${verdict} — ${t('pf.run_m_other')}` : verdict) + ended;
        // What a reading step came back with, on the step itself: "the locator matched" and
        // "it read the right thing" are different facts, and only the second one settles
        // why the operation looks the way it does.
        const value = run.value
            ? `<span class="fe-run fe-run--value" title="${esc(t('pf.run_read_value', { value: run.value }))}">= ${esc(run.value)}</span>`
            : '';
        return `${laneChip(run)}${value}<span class="fe-run fe-run--${run.mark}" title="${esc(title)}">${glyph}${text ? `<span>${esc(text)}</span>` : ''}</span>`;
    }
    /**
     * Which way a conditional went — on the node itself, because the lane it took may be
     * empty and then no step inside it is there to carry a mark. A repeat names no lane: how
     * it ended is in the verdict's tooltip, not a chip of its own.
     */
    function laneChip(run) {
        // No lane, but the engine said how the step ended ("until matched before pass 1/3",
        // "1 merchant_payout task(s)"): only the count is news on the row — the sentence is
        // prose that pushes the step's own name off it, so it stays in the tooltip.
        if (!run.lane) {
            const n = run.laneText?.match(/\d+\s*\/\s*\d+|\d+/)?.[0];
            return n
                ? `<span class="fe-run fe-run--lane" title="${esc(t('pf.run_lane', { text: run.laneText ?? '' }))}">→ ${esc(n.replace(/\s+/g, ''))}</span>`
                : '';
        }
        const full = t(`pf.lane_${run.lane}`);
        // A `for_each` names the row it took, which is a whole line of the app's own text —
        // it squeezed the step off the row, so the chip keeps the head of it and the
        // Context tab carries the sentence in full.
        const label = full.length > LANE_CHIP_MAX ? `${full.slice(0, LANE_CHIP_MAX - 1)}…` : full;
        const title = t('pf.run_lane', { text: run.laneText || full });
        return `<span class="fe-run fe-run--lane fe-run--lane-${esc(run.lane)}" title="${esc(title)}">→ ${esc(label)}</span>`;
    }
    /**
     * The steps a `run_deposit_flow` / `run_payout_flow` plays, when the flow it names is in
     * hand. Which list to look in comes from the op (FLOW_PICK_KIND), like the dropdown.
     */
    function playedSteps(n) {
        const kind = FLOW_PICK_KIND[n.op];
        if (!kind || !n.flow_id)
            return null;
        const from = kind === 'deposit' ? depositFlows : payoutFlows;
        return from.find((f) => f.id === Number(n.flow_id))?.flow.steps ?? null;
    }
    /**
     * The node at a VIEW path — one that may run through a played flow (`1/flow/17/then/0`).
     * `locate` knows only the document's own lanes and would plant a `flow: []` on the
     * step; this follows `flow` into the played document instead.
     */
    function nodeAtView(path) {
        const parts = path.split('/');
        let list = steps();
        for (let i = 0; list && i < parts.length; i += 2) {
            const node = list[Number(parts[i])];
            if (!node)
                return null;
            const lane = parts[i + 1];
            if (lane === undefined)
                return node;
            list = lane === 'flow' ? playedSteps(node) : (node[lane] ?? null);
        }
        return null;
    }
    function renderNode(n, path, index, readOnly = false) {
        // `readOnly` is "not this document's step" (a played flow, opened for reading);
        // `viewOnly` is "this whole page is a recorded run". Both drop the edit controls,
        // but only the first makes the row unselectable — in a run you still want to click
        // a step and read what it was configured with.
        const noEdit = readOnly || viewOnly;
        const spec = OPS[n.op];
        const sel = selectedPath === path ? ' fe-step--sel' : '';
        // Where the current run is, or how this step ended in it — see flow-run-panel.
        const run = runner?.step(path) ?? null;
        // Once a run has marked anything, a step it never reached is "not executed": its
        // own stripe would read as a state next to the green of the executed rows.
        const runCls = run ? ` fe-step--run-${run.mark}${run.other ? ' fe-step--run-other' : ''}`
            : runner?.hasMarks() ? ' fe-step--run-idle' : '';
        // Only the money steps are marked out; everything else keeps the neutral stripe.
        const cat = (spec ? '' : ' fe-step--unknown')
            + (CRITICAL_OPS.includes(n.op) ? ' fe-step--critical' : '');
        // What the row says under the type: the locator for a screen step, and for the
        // steps that have none, the thing they are about — the jump's destination, the
        // queue and the payout flow it plays, the deposit key, the value under test.
        const target = n.op === 'goto' ? `→ ${n.target ?? '?'}`
            : n.op === 'run_payout_flow' ? `${queueLabel(n.task_type)} → ${flowPickName(n.flow_id)}`
                : n.op === 'take_tasks' ? `${queueLabel(n.task_type || 'merchant_payout')} → ${t('pf.take_tasks_lane')}`
                    : n.op === 'run_deposit_flow' ? flowPickName(n.flow_id, depositFlows)
                        : n.op === 'take_deposits'
                            ? (DEPOSIT_FILTER_FIELDS.filter((k) => String(n[k] ?? '').trim())
                                .map((k) => `${k}=${String(n[k])}`).join(', ') || t('pf.dep_filters_none'))
                            : n.op === 'if_new' ? (n.key ?? '')
                                : n.op === 'if_match' ? `${n.value ?? ''} ~ ${n.regex ?? ''}`
                                    : n.op === 'report_deposit' ? (n.amount ?? '')
                                        : USSD_OPS.includes(n.op) ? (n.ussd ?? '')
                                            : n.op === 'wait_sms' ? (n.sms_success ?? []).join(' | ')
                                                : n.xpath ?? n.value ?? '';
        // A queue step with no payout flow picked pays nothing out. It is the one setting on
        // the step that cannot be guessed, so it is called out ON the step — the problems
        // banner is where you look when you already know something is wrong.
        // An imported reference counts as "no flow" here as well: it survives a save and a
        // launch, and only the phone finds out the flow it names does not exist.
        const badRef = !!n.flow_id && !isFlowRef(n.flow_id);
        const noFlow = !!FLOW_PICK_KIND[n.op] && (!n.flow_id || badRef);
        const noFlowWhy = badRef
            ? t('pf.err_flow_ref_imported', { op: opLabel(n.op), ref: String(n.flow_id) })
            : n.op === 'run_deposit_flow' ? t('pf.err_deposit_flow_pick') : t('pf.err_tasks_flow_pick');
        const chips = [
            noFlow ? `<span class="fe-chip fe-chip--warn" title="${esc(noFlowWhy)}">${esc(t('pf.no_flow_short'))}</span>` : '',
            n.value && spec?.fields.includes('value') && n.op !== 'if_match' ? `<span class="fe-chip fe-chip--val">${esc(n.value)}</span>` : '',
            n.var && spec?.fields.includes('var') ? `<span class="fe-chip fe-chip--val">{${esc(String(n.var).toUpperCase())}}</span>` : '',
            n.timeout ? `<span class="fe-chip fe-chip--ico" title="${esc(t('pf.timeout_short', { n: String(n.timeout) }))}">${clockIconSvg}${esc(String(n.timeout))}</span>` : '',
            n.best_effort ? `<span class="fe-chip fe-chip--ico" title="${esc(t('pf.best_effort_short'))}">${skipOnFailSvg}</span>` : '',
            n.id ? `<span class="fe-chip fe-chip--id">#${esc(n.id)}</span>` : '',
        ].join('');
        // A branch step (if_visible, repeat, …) carries whole sub-lists inside it. Folding
        // it collapses those lanes so a long flow stays readable; the chip keeps the size
        // of what is hidden visible.
        const branches = spec?.branches ?? [];
        // A played flow may play one of its own (auto → deposit → payout): opened the same
        // way, read-only all the way down, so a run's marks always have rows to land on.
        const played = playedSteps(n);
        const playedShown = !!played && playedOpen.has(n);
        const isFolded = branches.length > 0 && folded.has(n);
        const inner = played ? played.length
            : branches.reduce((sum, lane) => sum + (n[lane]?.length ?? 0), 0);
        // One control, two meanings: a branch step folds its own lanes, a `run_deposit_flow`
        // opens the flow it plays. Both are "show me what is inside this row".
        const fold = branches.length || played
            ? `<button type="button" class="fe-ico fe-fold" data-fold="${path}"
                   title="${esc(t(played ? (playedShown ? 'pf.collapse' : 'pf.expand') : (isFolded ? 'pf.expand' : 'pf.collapse')))}">${(played ? playedShown : !isFolded) ? '▾' : '▸'}</button>`
            : '<span class="fe-fold fe-fold--none"></span>';
        // Once the run has chosen, the lane it took lights up and the other one dims — the
        // list reads as the path the phone actually walked.
        const laneState = (lane) => !run?.lane ? ''
            : run.lane === lane ? ' fe-lane--taken' : ' fe-lane--skipped';
        const lanes = isFolded ? '' : branches.map((lane) => `
            <div class="fe-lane${laneState(lane)}">
                <span class="fe-lane__tag fe-lane__tag--${lane}">${esc(t(`pf.lane_${lane}`))}</span>
                <div class="fe-lane__body">
                    ${(n[lane] ?? []).map((c, i) => renderNode(c, `${path}/${lane}/${i}`, i, readOnly)).join('')}
                    ${noEdit ? '' : `<button type="button" class="fe-add" data-add="${path}/${lane}">+ ${esc(t('pf.add_step'))}</button>`}
                </div>
            </div>
        `).join('');
        // The played flow, read-only, under the step that plays it — same paths the engine
        // stamps, so the run lights these rows up too.
        const playedBody = !playedShown ? '' : `
            <div class="fe-lane fe-lane--played">
                <span class="fe-lane__tag fe-lane__tag--played">${esc(t('pf.lane_played'))}</span>
                <div class="fe-lane__body">
                    ${played.map((c, i) => renderNode(c, `${path}/flow/${i}`, i, true)).join('')}
                </div>
            </div>
        `;
        return `
            <div class="fe-step${sel}${cat}${runCls}${isFolded ? ' fe-step--folded' : ''}${readOnly ? ' fe-step--ro' : ''}" ${readOnly ? 'data-ro-path' : 'data-path'}="${path}" data-id="${esc(n.id ?? '')}">
                <div class="fe-step__row">
                    ${fold}
                    <span class="fe-step__n">${index + 1}</span>
                    <span class="fe-step__ico">${spec?.icon ?? ''}</span>
                    <span class="fe-step__main">
                        <span class="fe-step__op">${esc(opLabel(n.op))}${COMMIT_OPS.includes(n.op) ? ` <b class="fe-commit">${esc(t('pf.commit_flag'))}</b>` : ''}${n.comment ? `<span class="fe-step__cmt" title="${esc(n.comment)}">${esc(n.comment)}</span>` : ''}</span>
                        <span class="fe-step__target">${esc(String(target).slice(0, 90))}</span>
                    </span>
                    <span class="fe-step__chips">${(isFolded || (played && !playedShown)) ? `<span class="fe-chip">${esc(t('pf.n_inside', { n: String(inner) }))}</span>` : ''}${chips}${runChip(run, branches.length > 0 || !!played)}</span>
                    ${noEdit ? '' : `<span class="fe-step__btns">
                        <button type="button" class="fe-ico fe-ico--add" data-after="${path}" title="${esc(t('pf.add_after'))}">+</button>
                        <button type="button" class="fe-ico" data-up="${path}" title="${esc(t('pf.move_up'))}">↑</button>
                        <button type="button" class="fe-ico" data-down="${path}" title="${esc(t('pf.move_down'))}">↓</button>
                        <button type="button" class="fe-ico fe-ico--del" data-del="${path}" title="${esc(t('common.delete'))}">×</button>
                    </span>`}
                </div>
                ${run?.mark === 'err' && run.error
            ? `<div class="fe-step__err" title="${esc(run.error)}">${esc(run.error)}</div>` : ''}
                ${lanes}${playedBody}
            </div>
        `;
    }
    /**
     * Unfold whatever the run has walked into. With branches folded by default, a run
     * inside a lane would otherwise be invisible — the marks land on rows that are not
     * drawn. Returns whether this list holds the run.
     */
    function revealRun(list, base = '') {
        let holds = false;
        list.forEach((n, i) => {
            const path = base ? `${base}/${i}` : String(i);
            if (runner?.step(path)) {
                holds = true;
                // The run has walked into the flow this step plays; open it so its marks are
                // on rows that exist. Closed again by hand, like any other fold.
                if (playedSteps(n) && !byHand.has(n))
                    playedOpen.add(n);
            }
            let inner = false;
            for (const lane of OPS[n.op]?.branches ?? []) {
                if (n[lane])
                    inner = revealRun(n[lane], `${path}/${lane}`) || inner;
            }
            if (inner) {
                if (!byHand.has(n))
                    folded.delete(n);
                holds = true;
            }
            // …and down into the played flow, whose own played flows open the same way.
            const played = playedSteps(n);
            if (played && revealRun(played, `${path}/flow`)) {
                if (!byHand.has(n))
                    playedOpen.add(n);
                holds = true;
            }
        });
        return holds;
    }
    /** Just the step list — what a run repaints as it walks the flow. */
    function renderSteps() {
        // A recorded run has marks on every step it walked, so revealing them would open the
        // whole trace at once; there it stays an outline and the reader opens what interests.
        if (!viewOnly)
            revealRun(steps());
        stepsEl.innerHTML = steps().map((n, i) => renderNode(n, String(i), i)).join('')
            || `<p class="settings-hint">${esc(t('pf.no_steps'))}</p>`;
        // Follow the run down a long flow — but only when it MOVES, or every poll would
        // yank the page back while the author is reading somewhere else.
        const at = stepsEl.querySelector('.fe-step--run-at');
        // Either kind of row: the run walks into the played flow too, and following it there
        // is the point of showing those rows at all.
        const atPath = at?.dataset['path'] ?? at?.dataset['roPath'] ?? null;
        if (atPath && atPath !== followedPath)
            at.scrollIntoView({ block: 'nearest' });
        followedPath = atPath;
        requestAnimationFrame(drawArrows);
    }
    function render() {
        renderSteps();
        renderProblems();
        renderParams();
        renderPalette(); // its header names where the next pick lands, so it follows the selection
    }
    function renderProblems() {
        const problems = flowProblems(doc, record.task_type);
        if (!problems.length) {
            problemsEl.style.display = 'none';
            return;
        }
        problemsEl.style.display = '';
        problemsEl.innerHTML = `<b>${esc(t('pf.problems'))}</b><ul>${problems.map((p) => `<li>${esc(p)}</li>`).join('')}</ul>`;
    }
    /** Draw a jump arrow for every goto. Purely visual. */
    function drawArrows() {
        const box = canvasEl.getBoundingClientRect();
        arrowsEl.setAttribute('width', String(canvasEl.scrollWidth));
        arrowsEl.setAttribute('height', String(canvasEl.scrollHeight));
        const parts = [];
        root.querySelectorAll('.fe-step').forEach((el) => {
            const path = el.dataset['path'];
            if (path === undefined)
                return; // a row of the played flow — not ours to jump in
            const n = nodeAt(steps(), path);
            if (!n || n.op !== 'goto' || !n.target)
                return;
            const dest = Array.from(root.querySelectorAll('.fe-steps > .fe-step'))
                .find((d) => d.dataset['id'] === n.target);
            if (!dest)
                return;
            const a = el.getBoundingClientRect();
            const b = dest.getBoundingClientRect();
            const x = 8;
            const y1 = a.top - box.top + canvasEl.scrollTop + a.height / 2;
            const y2 = b.top - box.top + canvasEl.scrollTop + 8;
            const bow = Math.min(46, 14 + Math.abs(y2 - y1) / 6);
            parts.push(`<path d="M ${x} ${y1} C ${x - bow} ${y1}, ${x - bow} ${y2}, ${x} ${y2}" />`);
            parts.push(`<circle cx="${x}" cy="${y2}" r="3.5" class="fe-arrowhead" />`);
        });
        arrowsEl.innerHTML = parts.join('');
    }
    // ── parameters ─────────────────────────────────────────────────────────────
    function field(label, inner, hint = '') {
        return `<div class="fe-f"><label>${esc(label)}</label>${inner}${hint ? `<p class="settings-hint">${esc(hint)}</p>` : ''}</div>`;
    }
    /**
     * What the selected node type does, in words — its own tab in the step pane.
     *
     * The palette gives a node a two-word name ("Send the money"); this is where the author
     * finds out what the engine will actually do with it, without leaving the step.
     */
    function opDocHtml(op) {
        const doc = t(`pf.opdoc_${op}`);
        if (doc === `pf.opdoc_${op}`)
            return '';
        return `<div class="fe-opdoc"><b>${esc(opLabel(op))}</b><p>${esc(doc)}</p></div>`;
    }
    function renderParams() {
        const node = selectedPath ? nodeAt(steps(), selectedPath) : null;
        if (!node) {
            paramsEl.innerHTML = `<p class="settings-hint">${esc(t(viewOnly ? 'pf.select_step_view' : 'pf.select_step'))}</p>`;
            return;
        }
        const spec = OPS[node.op];
        const f = spec?.fields ?? [];
        // Four tabs, not one list of fourteen fields: what the step IS, what it acts on,
        // how it behaves, and what this type of step does at all. The choice survives the
        // re-render (paramTab) — this pane is redrawn on every keystroke.
        const stepRows = [];
        const rows = [];
        const messages = [];
        const behavior = [];
        // The type IS the step, so it is editable here rather than being a read-only echo
        // of what the palette picked: retyping a `tap` as a `wait` is a one-field change,
        // not a delete-and-rebuild. Shared parameters (xpath, timeout…) survive it.
        stepRows.push(field(t('pf.f_op'), `<select class="settings-input fe-i" data-k="op">
            ${categories.map((cat) => `
                <optgroup label="${esc(t(`pf.cat_${cat}`))}">
                    ${Object.keys(OPS).filter((op) => OPS[op].category === cat && offersOp(op)).map((op) => `<option value="${esc(op)}"${node.op === op ? ' selected' : ''}>${esc(opLabel(op))}</option>`).join('')}
                </optgroup>`).join('')}
            ${OPS[node.op] ? '' : `<option value="${esc(node.op)}" selected>${esc(node.op)}</option>`}
        </select>`, t('pf.op_hint')));
        if (f.includes('xpath')) {
            rows.push(field(t('pf.f_xpath'), `<textarea class="settings-input fe-i" data-k="xpath" rows="2">${esc(node.xpath ?? '')}</textarea>`, t('pf.xpath_hint')));
            // Web: inside a for_each the locator is scoped to the row; `page` looks across the
            // whole page instead (a details drawer the row opens outside itself).
            if (doc.platform === 'web') {
                rows.push(field(t('pf.f_page'), `<label class="fe-check"><input type="checkbox" class="fe-i" data-k="page"${node.page ? ' checked' : ''} /> ${esc(t('pf.page_hint'))}</label>`));
            }
        }
        if (f.includes('value')) {
            rows.push(field(t('pf.f_value'), `<input class="settings-input fe-i" data-k="value" list="fe-tokens" value="${esc(node.value ?? '')}" />
                 <datalist id="fe-tokens">${VALUE_TOKENS.map((v) => `<option value="{${v}}"></option>`).join('')}</datalist>`, t('pf.value_hint')));
        }
        if (f.includes('attr')) {
            rows.push(field(t('pf.f_attr'), `<select class="settings-input fe-i" data-k="attr">
                ${['text', 'content-desc'].map((a) => `<option${node.attr === a ? ' selected' : ''}>${a}</option>`).join('')}</select>`));
        }
        if (f.includes('parse')) {
            // Wallets that render the balance as prose ("Saldo, 2.949 pesos con 89
            // centavos") need it normalized to "<whole>,<cents>" before it can be read as a
            // number. ONE regex says how: group 1 is the whole units (every non-digit in it
            // is dropped, so a thousands separator needs no field of its own) and group 2,
            // if there is one, is the cents. The template/strip/pad fields this replaced
            // were three ways to say what the two groups already say.
            const p = (node.parse ?? {});
            const setParse = (k, v) => {
                const next = { ...(node.parse ?? {}) };
                if (v.trim() === '')
                    delete next[k];
                else
                    next[k] = v;
                if (Object.keys(next).length)
                    node.parse = next;
                else
                    delete node.parse;
            };
            rows.push(field(t('pf.f_parse_regex'), `<input class="settings-input fe-p" data-p="regex" value="${esc(String(p['regex'] ?? ''))}" placeholder="([\\d.]+)\\s*pesos(?:\\s*con\\s*(\\d+))?" />`, t('pf.parse_hint')));
            // stash the setter for the shared change handler below
            paramsEl._setParse = setParse;
        }
        if (f.includes('target')) {
            rows.push(field(t('pf.f_target'), `<select class="settings-input fe-i" data-k="target">
                <option value="">${esc(t('pf.pick_target'))}</option>
                ${steps().map((s, i) => {
                const chosen = node.target && (node.target === s.id) ? ' selected' : '';
                return `<option value="${esc(String(i))}"${chosen}>${esc(stepTitle(s, i))}</option>`;
            }).join('')}
            </select>`, t('pf.target_hint')));
        }
        if (f.includes('until')) {
            rows.push(field(t('pf.f_until'), `<textarea class="settings-input fe-i" data-k="until" rows="2">${esc(node.until ?? '')}</textarea>`, t('pf.until_hint')));
        }
        if (f.includes('key_template')) {
            rows.push(field(t('pf.f_key_template'), `<input class="settings-input fe-i" data-k="key_template" placeholder="//*[@text='{CHAR}']" value="${esc(node.key_template ?? '')}" />`, t('pf.key_template_hint')));
        }
        if (f.includes('glyphs')) {
            rows.push(field(t('pf.f_glyphs'), `<input class="settings-input fe-i" data-k="glyphs" placeholder="ing-au" value="${esc(node.glyphs ?? '')}" />`, t('pf.glyphs_hint')));
        }
        if (f.includes('outcome')) {
            rows.push(field(t('pf.f_outcome'), `<select class="settings-input fe-i" data-k="outcome">
                ${outcomeOptions(node.outcome).map((o) => `<option${node.outcome === o ? ' selected' : ''}>${o}</option>`).join('')}</select>`));
        }
        if (f.includes('on_absent')) {
            // On a success check "absent" is not a missing element, it is a transfer whose
            // fate nobody knows — a different sentence, so a different hint.
            rows.push(field(t('pf.f_on_absent'), `<select class="settings-input fe-i" data-k="on_absent">
                ${outcomeOptions(node.on_absent).map((o) => `<option${node.on_absent === o ? ' selected' : ''}>${o}</option>`).join('')}</select>`, t(node.op === 'verify_success' ? 'pf.success_absent_hint' : 'pf.on_absent_hint')));
        }
        if (f.includes('times'))
            behavior.push(field(t('pf.f_times'), `<input class="settings-input fe-i" data-k="times" type="number" min="1" value="${esc(String(node.times ?? 1))}" />`));
        if (f.includes('timeout'))
            behavior.push(field(t('pf.f_timeout'), `<input class="settings-input fe-i" data-k="timeout" type="number" min="0" value="${esc(String(node.timeout ?? ''))}" />`));
        if (f.includes('minmax')) {
            behavior.push(field(t('pf.f_pause'), `<span class="fe-pair">
                <input class="settings-input fe-i" data-k="min" type="number" step="0.1" value="${esc(String(node.min ?? 1))}" />
                <input class="settings-input fe-i" data-k="max" type="number" step="0.1" value="${esc(String(node.max ?? 2))}" /></span>`));
        }
        if (f.includes('package')) {
            // A step with no package of its own runs the flow's package. Show that resolved
            // value in the field — as a placeholder it read as "nothing is set here", when in
            // fact the step is fully configured. Editing it writes a step-level override.
            const inherited = !node.package && !!doc.package;
            rows.push(field(t('pf.f_package'), `<input class="settings-input fe-i" data-k="package" value="${esc(node.package ?? doc.package ?? '')}" />`, inherited ? t('pf.package_inherited') : ''));
        }
        if (f.includes('name'))
            rows.push(field(t('pf.f_name'), `<input class="settings-input fe-i" data-k="name" value="${esc(node.name ?? '')}" />`));
        if (f.includes('replace')) {
            rows.push(field(t('pf.f_replace'), `<label class="fe-check"><input type="checkbox" class="fe-i" data-k="replace"${node.replace ? ' checked' : ''} /> ${esc(t('pf.replace_hint'))}</label>`));
        }
        // ── USSD: the code, and the country code a payout takes off {ACCOUNT} ──
        if (f.includes('ussd')) {
            rows.push(field(t('pf.f_ussd'), `<input class="settings-input fe-i" data-k="ussd" list="fe-tokens" value="${esc(node.ussd ?? '')}" placeholder="${esc(node.op === 'ussd_pay' ? USSD_PAY_DEFAULT : '#149*72*{PIN}#')}" />`, t(node.op === 'ussd_pay' ? 'pf.ussd_pay_hint' : 'pf.ussd_hint')));
        }
        if (f.includes('ussd_fail')) {
            rows.push(field(t('pf.f_ussd_fail'), `<textarea class="settings-input fe-i fe-sms" data-k="ussd_fail" rows="2" spellcheck="false">${esc((node.ussd_fail ?? []).join('\n'))}</textarea>`, t(node.op === 'ussd_pay' ? 'pf.ussd_fail_pay_hint' : 'pf.ussd_fail_hint')));
        }
        if (f.includes('ussd_pay')) {
            rows.push(field(t('pf.f_country_code'), `<input class="settings-input fe-i" data-k="country_code" value="${esc(String(node.country_code ?? ''))}" placeholder="237" />`, t('pf.country_code_hint')));
        }
        // ── The SMS that settles a USSD transfer: the patterns and how long to wait ──
        if (f.includes('sms')) {
            const lines = (v) => esc((v ?? []).join('\n'));
            rows.push(field(t('pf.f_sms_success'), `<textarea class="settings-input fe-i fe-sms" data-k="sms_success" rows="5" spellcheck="false">${lines(node.sms_success)}</textarea>`, t('pf.sms_success_hint')));
            rows.push(field(t('pf.f_sms_decline'), `<textarea class="settings-input fe-i fe-sms" data-k="sms_decline" rows="2" spellcheck="false">${lines(node.sms_decline)}</textarea>`, t('pf.sms_decline_hint')));
            rows.push(field(t('pf.f_sms_timeout'), `<input class="settings-input fe-i" data-k="sms_timeout" type="number" min="1" step="1" value="${esc(String(node.sms_timeout ?? ''))}" placeholder="${SMS_TIMEOUT_DEFAULT}" />`, t('pf.sms_timeout_hint')));
        }
        // ── the list walk and what a row yields ──
        if (f.includes('regex')) {
            rows.push(field(t('pf.f_regex'), `<input class="settings-input fe-i" data-k="regex" value="${esc(node.regex ?? '')}" placeholder="${esc(node.op === 'if_match' ? '^\\+' : '([\\d.,]+)')}" />`, t(node.op === 'if_match' ? 'pf.regex_match_hint' : node.op === 'ussd' ? 'pf.regex_ussd_hint' : node.op === 'ussd_pay' ? 'pf.regex_ussd_pay_hint' : 'pf.regex_read_hint')));
        }
        if (f.includes('var')) {
            rows.push(field(t('pf.f_var'), `<input class="settings-input fe-i" data-k="var" value="${esc(node.var ?? '')}" placeholder="${node.op === 'ussd_pay' ? 'USSD_REPLY' : 'COELSA_ID'}" />`, t('pf.var_hint')));
        }
        // Where the drag starts and ends, as a fraction of the screen height — a sheet is
        // pulled up (0.8 -> 0.3), a list pulled down (0.3 -> 0.8).
        if (f.includes('swipe_drag')) {
            rows.push(field(t('pf.f_from_y'), `<input class="settings-input fe-i" data-k="from_y" type="number" min="0" max="1" step="0.05" value="${esc(String(node.from_y ?? 0.8))}" />`, t('pf.swipe_drag_hint')));
            rows.push(field(t('pf.f_to_y'), `<input class="settings-input fe-i" data-k="to_y" type="number" min="0" max="1" step="0.05" value="${esc(String(node.to_y ?? 0.3))}" />`));
            behavior.push(field(t('pf.f_duration'), `<input class="settings-input fe-i" data-k="duration" type="number" min="50" step="50" value="${esc(String(node.duration ?? 600))}" />`, t('pf.duration_hint')));
        }
        // The commit's second gesture: a slider dragged when the tap button above is not the
        // one the wallet drew. Left empty, the commit is a plain tap.
        if (f.includes('commit_swipe')) {
            COMMIT_SWIPE_FIELDS.forEach((k) => {
                rows.push(field(t(`pf.f_${k}`), `<textarea class="settings-input fe-i" data-k="${k}" rows="2">${esc(String(node[k] ?? ''))}</textarea>`, t(`pf.${k}_hint`)));
            });
            behavior.push(field(t('pf.f_probe_timeout'), `<input class="settings-input fe-i" data-k="probe_timeout" type="number" min="1" value="${esc(String(node.probe_timeout ?? 5))}" />`, t('pf.probe_timeout_hint')));
        }
        if (f.includes('key')) {
            // notify_tg re-uses `key` as its per-device throttle key; a deposit op as the
            // reported-deposit id. The placeholder differs by op.
            rows.push(field(t('pf.f_key'), `<input class="settings-input fe-i" data-k="key" value="${esc(node.key ?? '')}" placeholder="${node.op === 'notify_tg' ? 'ing-logout' : '{COELSA_ID}'}" />`, t(node.op === 'notify_tg' ? 'pf.key_notify_hint' : 'pf.key_hint')));
        }
        // ── web ops ──
        if (f.includes('match')) {
            rows.push(field(t('pf.f_match'), `<input class="settings-input fe-i" data-k="match" value="${esc(String(node.match ?? ''))}" placeholder="login" />`, t('pf.match_hint')));
        }
        if (f.includes('text')) {
            rows.push(field(t('pf.f_text'), `<textarea class="settings-input fe-i" data-k="text" rows="2" placeholder="${esc('ING session expired — operator must log in')}">${esc(String(node.text ?? ''))}</textarea>`, t('pf.text_hint')));
        }
        if (f.includes('cooldown_sec')) {
            behavior.push(field(t('pf.f_cooldown'), `<input class="settings-input fe-i" data-k="cooldown_sec" type="number" min="0" value="${esc(String(node.cooldown_sec ?? 1800))}" />`, t('pf.cooldown_hint')));
        }
        if (f.includes('tag_operator')) {
            behavior.push(field(t('pf.f_tag_operator'), `<label class="fe-check"><input type="checkbox" class="fe-i" data-k="tag_operator"${node.tag_operator === false ? '' : ' checked'} /> ${t('pf.tag_operator_label')}</label>`));
        }
        if (f.includes('max_scrolls')) {
            behavior.push(field(t('pf.f_max_scrolls'), `<input class="settings-input fe-i" data-k="max_scrolls" type="number" min="0" value="${esc(String(node.max_scrolls ?? 2))}" />`, t('pf.max_scrolls_hint')));
        }
        // ── the task queues and the payout flow a lane plays ──
        if (f.includes('task_type')) {
            // The flow's own request defaults to the merchant queue (an unset value reads as
            // that); a "Run payout flow" that hands a queue over overrides it at play time.
            const own = node.op === 'take_tasks';
            const picked = node.task_type || (own ? 'merchant_payout' : '');
            rows.push(field(t('pf.f_queue'), `<select class="settings-input fe-i" data-k="task_type">
                ${QUEUES.map((q) => `<option value="${q}"${picked === q ? ' selected' : ''}>${esc(queueLabel(q))}</option>`).join('')}
            </select>`, t(own ? 'pf.take_tasks_queue_hint' : 'pf.queue_hint')));
        }
        if (f.includes('flow_id')) {
            // Two kinds of pick in one dropdown: the app's default flow, which says which
            // flow that is right now, or a flow pinned by id. No "— choose a flow —" row: an
            // empty pick is not an option to offer, it is a mistake, and the step itself
            // flags it (renderNode's warn chip).
            // Which kind of flow this step plays comes from the op: the queues play payouts,
            // the auto flow's own step plays a deposit pass. `default` is a mark per (app,
            // task type), so the two lists resolve it to different flows.
            const deposit = FLOW_PICK_KIND[node.op] === 'deposit';
            rows.push(field(t(deposit ? 'pf.f_deposit_flow' : 'pf.f_payout_flow'), `<select class="settings-input fe-i" data-k="flow_id">${flowOptions(node.flow_id, deposit ? depositFlows : payoutFlows)}</select>`, t(deposit ? 'pf.deposit_flow_hint' : 'pf.payout_flow_hint')));
        }
        // ── the deposit list request: every filter optional, each a template ──
        if (f.includes('deposit_filters')) {
            for (const k of DEPOSIT_FILTER_FIELDS) {
                rows.push(field(t(`pf.f_depf_${k}`), `<input class="settings-input fe-i" data-k="${k}" value="${esc(String(node[k] ?? ''))}" placeholder="${esc(t(`pf.depf_${k}_ph`))}" />`, t(`pf.depf_${k}_hint`)));
            }
        }
        // ── the deposit report: every field a template over what the steps read ──
        if (f.includes('deposit')) {
            for (const k of DEPOSIT_FIELDS) {
                rows.push(field(t(`pf.f_dep_${k}`), `<input class="settings-input fe-i" data-k="${k}" value="${esc(String(node[k] ?? ''))}" placeholder="${esc(t(`pf.dep_${k}_ph`))}" />`, t(`pf.dep_${k}_hint`)));
            }
            rows.push(field(t('pf.f_attach_screenshot'), `<label class="fe-check"><input type="checkbox" class="fe-i" data-k="attach_screenshot"${node.attach_screenshot ? ' checked' : ''} /> ${esc(t('pf.attach_screenshot_hint'))}</label>`));
        }
        // This step has no settings of its own, and a pane with nothing in it reads as "there
        // is nothing to configure" rather than "the bounds live elsewhere" — say where.
        if (node.op === 'if_balance_band') {
            stepRows.push(field(t('pf.f_balance_band'), `<p class="settings-hint">${esc(t('pf.balance_band_where'))}</p>`));
        }
        // Every step takes a comment: a flow is thirty locators deep and "what is this step
        // for" is the question its author will be asked first. It is a note to the next
        // author; what the OPERATOR reads is the Message tab.
        stepRows.push(field(t('pf.f_comment'), `<input class="settings-input fe-i" data-k="comment" value="${esc(node.comment ?? '')}" />`, t('pf.comment_hint')));
        // ── Message tab: what this step puts into the operation's comment ───────────
        // The commit step is the exception: it never contributes a failure of its own to
        // report — it is the LINE failures are reported around — so instead of a fail
        // message it owns the two wordings for either side of itself.
        if (f.includes('commit_msgs')) {
            // Left empty, the engine's own wording is used — which is why the default is the
            // placeholder and not the value: a prefilled field would be saved into the flow
            // and then quietly stop following the engine when that wording is improved.
            // A USSD transfer is sent, not tapped — same fields, its own wording.
            const sfx = node.op === 'ussd_pay' ? '_ussd' : '';
            COMMIT_MSG_FIELDS.forEach((k) => {
                messages.push(field(t(`pf.f_${k}${sfx}`), `<input class="settings-input fe-i" data-k="${k}" value="${esc(String(node[k] ?? ''))}" placeholder="${esc(COMMIT_MSG_DEFAULTS[k] ?? '')}" />`, t(`pf.${k}_hint${sfx}`)));
            });
        }
        else if (ENDS_PAYOUT.includes(node.op)) {
            // The sentence a step that ENDS the payout is reported with. Only these ops
            // carry one: everywhere else the step is not the thing being explained — the
            // driver's error and the step path already say what broke and where.
            messages.push(field(t('pf.f_fail_msg'), `<input class="settings-input fe-i" data-k="fail_msg" value="${esc(String(node.fail_msg ?? ''))}" placeholder="${esc(t('pf.fail_msg_placeholder'))}" />`, t('pf.fail_msg_end_hint')));
        }
        behavior.push(field(t('pf.f_best_effort'), `<label class="fe-check"><input type="checkbox" class="fe-i" data-k="best_effort"${node.best_effort ? ' checked' : ''} /> ${esc(t('pf.best_effort_hint'))}</label>`));
        behavior.push(field(t('pf.f_id'), `<input class="settings-input fe-i" data-k="id" value="${esc(node.id ?? '')}" />`, t('pf.id_hint')));
        // Tabs, not stacked sections: the pane is a 360px column, and three folded headers
        // with the fields underneath meant scrolling past what you were not editing. The
        // groups that have no fields for this op are not offered at all.
        const groups = [
            { key: 'step', title: t('pf.grp_step'), rows: stepRows },
            { key: 'element', title: t('pf.grp_element'), rows },
            { key: 'message', title: t('pf.grp_message'), rows: messages },
            { key: 'behavior', title: t('pf.grp_behavior'), rows: behavior },
            { key: 'info', title: t('pf.grp_info'), rows: [opDocHtml(node.op)] },
            // Only a recorded run has these: what every {TOKEN} stood for when this step
            // ran. A step's locator and value are written in tokens, so "the xpath is
            // right" and "the xpath was right for THIS row" are different questions.
            { key: 'tokens', title: t('pf.grp_tokens'), rows: [tokensHtml(selectedPath)] },
        ].filter((g) => g.rows.some(Boolean));
        if (!groups.some((g) => g.key === paramTab))
            paramTab = 'step';
        paramsEl.innerHTML = `
            <div class="fe-ptabs">
                ${groups.map((g) => `<button type="button" class="fe-ptab${g.key === paramTab ? ' active' : ''}" data-p="${g.key}">${esc(g.title)}</button>`).join('')}
            </div>
            <div class="fe-pgroup">${(groups.find((g) => g.key === paramTab)?.rows ?? []).join('')}</div>`;
        // The tab row scrolls and is rebuilt on every click: bring the chosen tab back into view.
        const tabsEl = paramsEl.querySelector('.fe-ptabs');
        const activeTab = tabsEl?.querySelector('.fe-ptab.active');
        if (tabsEl && activeTab) {
            tabsEl.scrollLeft = Math.max(0, activeTab.offsetLeft + activeTab.offsetWidth - tabsEl.clientWidth);
        }
        paramsEl.querySelectorAll('.fe-ptab').forEach((b) => {
            b.addEventListener('click', () => { paramTab = b.dataset['p']; renderParams(); });
        });
        // A recorded run shows the step's settings as they RAN — the same pane, with every
        // field locked. Disabled rather than a second read-only pane: one description of a
        // step's parameters, and the browser makes it uneditable for free. The group tabs
        // above stay live; they only choose what is on screen.
        if (viewOnly) {
            paramsEl.querySelectorAll('.fe-pgroup input, .fe-pgroup select, .fe-pgroup textarea, .fe-pgroup button').forEach((el) => { el.disabled = true; });
        }
        paramsEl.querySelectorAll('.fe-p').forEach((input) => {
            input.addEventListener('change', () => {
                const setParse = paramsEl._setParse;
                setParse?.(input.dataset['p'], input.value);
                markDirty();
                render();
            });
        });
        paramsEl.querySelectorAll('.fe-i').forEach((input) => {
            const commit = () => {
                const key = input.dataset['k'];
                const el = input;
                if (key === 'op') {
                    const next = el.value;
                    if (next === node.op)
                        return;
                    // Lanes belong to the type: a `tap` has nowhere to keep an if's then/else.
                    // Never drop authored steps silently — ask, and keep the type unchanged
                    // when the answer is no.
                    const lanes = ['then', 'else', 'do'];
                    const keeps = new Set(OPS[next]?.branches ?? []);
                    const losing = lanes.filter((l) => !keeps.has(l) && (node[l]?.length ?? 0) > 0);
                    if (losing.length && !confirm(t('pf.confirm_op_change'))) {
                        render();
                        return;
                    }
                    for (const l of losing)
                        delete node[l];
                    node.op = next;
                }
                else if (key === 'target') {
                    // The dropdown carries the destination's INDEX; convert it to a label,
                    // creating one on the destination when it has none yet.
                    const idx = Number(el.value);
                    if (!Number.isInteger(idx) || !steps()[idx]) {
                        delete node.target;
                    }
                    else {
                        const taken = new Set(steps().map((s) => s.id).filter(Boolean));
                        node.target = ensureId(steps()[idx], taken);
                    }
                }
                else if (key === 'flow_id') {
                    // A dropdown of flows: the option value is the registry id, or `default`
                    // — stored as written, never resolved to an id here. Pinning today's
                    // winner is exactly what that pick is NOT.
                    const id = Number(el.value);
                    if (Number.isInteger(id) && id > 0)
                        node[key] = id;
                    else
                        delete node[key];
                }
                else if (key === 'sms_success' || key === 'sms_decline' || key === 'ussd_fail') {
                    // One regex per line, stored as a list — the engine's shape.
                    const list = el.value.split('\n').map((l) => l.trim()).filter(Boolean);
                    if (list.length)
                        node[key] = list;
                    else
                        delete node[key];
                }
                else if (el.type === 'checkbox') {
                    const on = el.checked;
                    if (on)
                        node[key] = true;
                    else
                        delete node[key];
                }
                else if (el.type === 'number') {
                    const v = el.value.trim();
                    if (v === '')
                        delete node[key];
                    else
                        node[key] = Number(v);
                }
                else {
                    const v = el.value.trim();
                    if (v === '')
                        delete node[key];
                    else
                        node[key] = v;
                }
                markDirty();
                render();
            };
            input.addEventListener('change', commit);
        });
    }
    // ── History tab: every save, and the way back to any of them ───────────────
    // Fetched each time the tab is opened — a save just happened, or someone else's did.
    // The newest version IS the stored flow, so it has no Restore; restoring an older one
    // is a server-side save (recorded as a version of its own) that the editor then loads,
    // replacing what is on screen — hence the confirmation when there are unsaved edits.
    async function renderHistory() {
        historyEl.innerHTML = `<p class="settings-hint">${esc(t('common.loading'))}</p>`;
        let versions;
        try {
            versions = await fetchFlowHistory(record.id);
        }
        catch (e) {
            historyEl.innerHTML = `<p class="settings-error">${esc(e.message)}</p>`;
            return;
        }
        if (!versions.length) {
            historyEl.innerHTML = `<p class="settings-hint">${esc(t('pf.history_empty'))}</p>`;
            return;
        }
        const numberOf = new Map(versions.map((v) => [v.id, v.n]));
        const when = (iso) => new Date(iso).toLocaleString([], { hour12: false });
        historyEl.innerHTML = `
            <p class="settings-hint">${esc(t('pf.history_hint'))}</p>
            <div class="fe-hist">
                ${versions.map((v, i) => `
                    <div class="fe-hist__row${i === 0 ? ' fe-hist__row--current' : ''}">
                        <span class="fe-hist__n">#${v.n}</span>
                        <span class="fe-hist__when">${esc(when(v.saved_at))}</span>
                        <span class="fe-hist__who">${esc(v.saved_by_login ?? '—')}</span>
                        <span class="fe-hist__what">
                            ${esc(v.name)} · ${esc(t('pf.history_steps', { n: String(v.steps) }))}
                            ${v.restored_from !== null ? `<span class="fe-chip">${esc(t('pf.history_restored_from', { n: String(numberOf.get(v.restored_from) ?? '?') }))}</span>` : ''}
                            ${i === 0 ? `<span class="fe-chip fe-chip--val">${esc(t('pf.history_current'))}</span>` : ''}
                        </span>
                        <span class="fe-hist__act">${i === 0 ? '' : `<button type="button" class="modal__cancel fe-hist__restore" data-vid="${v.id}">${esc(t('pf.history_restore'))}</button>`}</span>
                    </div>`).join('')}
            </div>`;
        historyEl.querySelectorAll('.fe-hist__restore').forEach((b) => {
            b.addEventListener('click', async () => {
                const vid = Number(b.dataset['vid']);
                const n = numberOf.get(vid) ?? vid;
                if (!confirm(t(dirty ? 'pf.history_confirm_restore_dirty' : 'pf.history_confirm_restore', { n: String(n) })))
                    return;
                errEl.style.display = 'none';
                b.disabled = true;
                try {
                    const restored = await restoreFlowVersion(record.id, vid);
                    // What the server now holds is what the editor shows: document and record
                    // fields alike, clean (nothing unsaved), folded like a freshly opened flow.
                    record = restored;
                    doc = JSON.parse(JSON.stringify(restored.flow ?? {}));
                    doc.app = doc.app || restored.app;
                    if (!Array.isArray(doc.steps))
                        doc.steps = [];
                    normalizeLoadedFlow(doc);
                    foldBranches(doc.steps);
                    general.name = restored.name;
                    general.note = restored.note;
                    titleEl.textContent = general.name;
                    document.getElementById('page-title').textContent = general.name;
                    selectedPath = null;
                    addTarget = null;
                    dirty = false;
                    savedEl.textContent = t('pf.history_restored', { n: String(n) });
                    showTab('flow');
                    render();
                }
                catch (err) {
                    b.disabled = false;
                    errEl.textContent = err.message;
                    errEl.style.display = 'block';
                }
            });
        });
    }
    // ── Info tab: what a flow author has to look up ────────────────────────────
    // The substitution reference used to hang under every locator field, where it buried
    // the step's own parameters and still had to be re-read for each one. It is reference
    // material, so it lives on a page of its own; clicking a token copies it.
    /**
     * The amount's formats: what goes after the colon, and what 123456 comes out as.
     *
     * Named specs and pictures are listed apart. A picture is not a name to memorise — it
     * is read off itself, so the rule that makes it readable ("`0`/`#` are digits, the
     * picture's own punctuation is the output's") is said ONCE over the block instead of
     * being repeated, identically and uselessly, on every row.
     */
    function amountFormatsHtml() {
        const rows = (kind) => AMOUNT_FORMATS
            .filter((f) => f.kind === kind)
            .map((f) => `
                <div class="fe-info__tok fe-info__tok--xpath" data-tok="${esc(f.spec ? `AMOUNT:${f.spec}` : 'AMOUNT')}">
                    <code>{AMOUNT${f.spec ? `:${esc(f.spec)}` : ''}}</code>
                    <span class="fe-info__doc">${esc(t(f.doc))}</span>
                    <span class="fe-info__fit">${esc(f.example)}</span>
                </div>`).join('');
        return `
            <div class="fe-info__cat">${esc(t('pf.tokgroup_formats'))}</div>
            <p class="settings-hint fe-info__why">${esc(t('pf.fmt_why'))}</p>
            <div class="fe-info__t">${rows('named')}</div>
            <div class="fe-info__cat">${esc(t('pf.fmt_picture_cat'))}</div>
            <p class="settings-hint fe-info__why">${esc(t('pf.fmt_picture_why'))}</p>
            <div class="fe-info__t">${rows('picture')}</div>`;
    }
    /**
     * What a flow can END with, and what the commit tap changes about it.
     *
     * This is the half of the model that is invisible in the step list: `commit` is drawn as
     * one more tap, but it is the line between "nothing happened, retry it" and "the money
     * is out, a human has to look" — and no crash after it may be reported as a clean
     * cancel. See flow_runner.run_payout.
     */
    function outcomesHtml() {
        const row = (code, doc) => `
            <div class="fe-info__row">
                <code>${esc(code)}</code>
                <span class="fe-info__doc">${esc(doc)}</span>
            </div>`;
        return `
            <h3>${esc(t('pf.outcomes_title'))}</h3>
            <p class="settings-hint">${esc(t('pf.outcomes_hint'))}</p>
            <div class="fe-info__t fe-info__t--2">
                ${ /* SUCCESS is documented but not in OUTCOMES: it is what a finished run
              reports, never something a step may declare. */''}
                ${['SUCCESS', ...OUTCOMES].map((o) => row(o, t(`pf.outdoc_${o}`))).join('')}
            </div>
            <div class="fe-info__cat">${esc(t('pf.commit_line_title'))}</div>
            <p class="settings-hint fe-info__why">${esc(t('pf.commit_line_why'))}</p>
            <div class="fe-info__t fe-info__t--2">
                ${row(t('pf.commit_before'), t('pf.commit_before_doc'))}
                ${row(t('pf.commit_after'), t('pf.commit_after_doc'))}
            </div>
            <div class="fe-info__cat">${esc(t('pf.commit_msg_title'))}</div>
            <p class="settings-hint fe-info__why">${esc(t('pf.commit_msg_why'))}</p>
            <div class="fe-info__msgs">
                ${[
            ['pf.commit_msg_before_ex', 'pf.commit_msg_before_note'],
            ['pf.commit_msg_after_ex', 'pf.commit_msg_after_note'],
            ['pf.commit_msg_cancel_ex', 'pf.commit_msg_cancel_note'],
            ['pf.commit_msg_decline_ex', 'pf.commit_msg_decline_note'],
        ].map(([ex, note]) => `
                    <div class="fe-info__msg">
                        <code>${esc(t(ex))}</code>
                        <span class="fe-info__doc">${esc(t(note))}</span>
                    </div>`).join('')}
            </div>`;
    }
    function renderInfo() {
        const inXpath = new Set(XPATH_TOKENS);
        const inValue = new Set(VALUE_TOKENS);
        const fit = (tok) => [
            inXpath.has(tok) ? t('pf.info_fit_xpath') : '',
            inValue.has(tok) ? t('pf.info_fit_value') : '',
        ].filter(Boolean).join(' · ');
        infoEl.innerHTML = `
            <div class="fe-info">
                <h3>${esc(t('pf.tokens_title'))}</h3>
                <p class="settings-hint">${esc(t('pf.tokens_hint'))}</p>
                ${TOKEN_GROUPS.map((g) => `
                    <div class="fe-info__cat">${esc(t(`pf.tokgroup_${g.key}`))}</div>
                    ${g.key === 'amount' ? `<p class="settings-hint fe-info__why">${esc(t('pf.tokgroup_amount_why'))}</p>` : ''}
                    <div class="fe-info__t">
                        ${g.tokens.map((tok) => `
                            <div class="fe-info__tok" data-tok="${esc(tok)}">
                                <code>{${esc(tok)}}</code>
                                <span class="fe-info__doc">${esc(tokenDoc(tok))}</span>
                                <span class="fe-info__fit">${esc(fit(tok))}</span>
                            </div>`).join('')}
                    </div>
                    ${g.key === 'amount' ? amountFormatsHtml() : ''}`).join('')}
                ${outcomesHtml()}
                <div class="fe-info__cat">${esc(t('pf.tokgroup_step'))}</div>
                <p class="settings-hint fe-info__why">${esc(t('pf.tokgroup_step_why'))}</p>
                <div class="fe-info__t">
                    <div class="fe-info__tok" data-tok="CHAR">
                        <code>{CHAR}</code>
                        <span class="fe-info__doc">${esc(t('pf.tok_char'))}</span>
                        <span class="fe-info__fit">${esc(t('pf.info_fit_key'))}</span>
                    </div>
                </div>
            </div>`;
        infoEl.querySelectorAll('.fe-info__tok').forEach((row) => {
            row.addEventListener('click', () => {
                const tok = `{${row.dataset['tok']}}`;
                void navigator.clipboard?.writeText(tok).catch(() => { });
                const fitEl = row.querySelector('.fe-info__fit');
                const was = fitEl.textContent;
                fitEl.textContent = t('pf.tok_copied');
                row.classList.add('fe-info__tok--copied');
                setTimeout(() => { fitEl.textContent = was; row.classList.remove('fe-info__tok--copied'); }, 1200);
            });
        });
    }
    // ── General tab: the flow as a record ──────────────────────────────────────
    /** What the teams dropdown shows when it is closed. */
    function teamsLabel() {
        if (!general.teamIds.length)
            return t('pf.teams_none');
        const names = general.teamIds
            .map((id) => scopes.teams.find((tm) => tm.id === id)?.name ?? String(id));
        return names.length <= 3 ? names.join(', ') : t('pf.teams_n', { n: String(names.length) });
    }
    function renderGeneral() {
        generalEl.innerHTML = `
            <div class="fe-gen">
                ${field(t('pf.name'), `<input class="settings-input fe-s" data-s="name" maxlength="80" value="${esc(general.name)}" />`)}
                ${field(t('pf.note'), `<input class="settings-input fe-s" data-s="note" maxlength="200" value="${esc(general.note)}" />`)}
                ${field(t('pf.enabled'), `<label class="fe-check"><input type="checkbox" class="fe-s" data-s="enabled"${general.enabled ? ' checked' : ''} /> ${esc(t('pf.enabled_hint'))}</label>`)}
                ${field(t('pf.default_pick'), `<label class="fe-check"><input type="checkbox" class="fe-s" data-s="defaultPick"${general.defaultPick ? ' checked' : ''} /> ${esc(t('pf.default_pick_hint'))}</label>`)}
                ${field(t('pf.f_platform'), `<label class="fe-check"><input type="checkbox" class="fe-s" data-s="platform"${doc.platform === 'web' ? ' checked' : ''} /> ${esc(t('pf.platform_web_label'))}</label>`, t('pf.platform_hint'))}
                ${field(t('pf.f_file'), `<div class="fe-io">
                    <button type="button" class="modal__cancel fe-export">${esc(t('pf.export_json'))}</button>
                    <button type="button" class="modal__cancel fe-import">${esc(t('pf.import_json'))}</button>
                </div>`, t('pf.file_hint'))}
                ${field(t('pf.teams'), scopes.teams.length
            ? `<div class="fe-ms">
                           <button type="button" class="settings-input fe-ms__btn">
                               <span class="fe-ms__label">${esc(teamsLabel())}</span><span class="fe-ms__caret">▾</span>
                           </button>
                           <div class="fe-ms__pop" hidden>
                               ${scopes.teams.map((tm) => `<label class="fe-ms__opt"><input type="checkbox" class="fe-team-cb" value="${tm.id}"${general.teamIds.includes(tm.id) ? ' checked' : ''} /> ${esc(tm.name)}</label>`).join('')}
                           </div>
                       </div>`
            : `<p class="settings-hint">${esc(t('pf.no_teams'))}</p>`, t('pf.teams_hint'))}
            </div>
        `;
        // Export writes what is ON SCREEN, unsaved edits included: the file is a snapshot of
        // the flow you are looking at, which is the point of taking one before a risky
        // change. Import replaces the DOCUMENT only — the record's name, note and team
        // assignment stay put, so pulling a newer version of the steps into a flow that is
        // already live never silently re-points it at other teams.
        generalEl.querySelector('.fe-export').addEventListener('click', () => {
            downloadFlowFile({
                task_type: record.task_type, app: doc.app || record.app,
                name: general.name, note: general.note, flow: doc,
            });
        });
        generalEl.querySelector('.fe-import').addEventListener('click', async () => {
            errEl.style.display = 'none';
            try {
                const file = await pickFlowFile();
                if (!file)
                    return;
                if (!confirm(t('pf.confirm_import', { n: String(file.flow.steps.length) })))
                    return;
                doc = file.flow;
                normalizeLoadedFlow(doc);
                foldBranches(doc.steps);
                record.app = doc.app;
                selectedPath = null;
                addTarget = null;
                markDirty();
                showTab('flow'); // the steps are what changed — show them
                render();
            }
            catch (err) {
                errEl.textContent = err.message;
                errEl.style.display = 'block';
            }
        });
        generalEl.querySelectorAll('.fe-s').forEach((input) => {
            input.addEventListener('change', () => {
                const k = input.dataset['s'];
                if (k === 'name') {
                    general.name = input.value.trim();
                    titleEl.textContent = general.name;
                }
                else if (k === 'note')
                    general.note = input.value.trim();
                else if (k === 'enabled')
                    general.enabled = input.checked;
                else if (k === 'defaultPick')
                    general.defaultPick = input.checked;
                else if (k === 'platform') {
                    // Switching to a web flow changes which ops the palette offers, so
                    // re-render the editor, not just this tab.
                    if (input.checked)
                        doc.platform = 'web';
                    else
                        delete doc.platform;
                    markDirty();
                    renderGeneral();
                    render();
                    return;
                }
                markDirty();
            });
        });
        const ms = generalEl.querySelector('.fe-ms');
        if (ms) {
            const pop = ms.querySelector('.fe-ms__pop');
            const label = ms.querySelector('.fe-ms__label');
            ms.querySelector('.fe-ms__btn').addEventListener('click', (e) => {
                e.stopPropagation();
                pop.hidden = !pop.hidden;
            });
            // Clicking anywhere else closes it; ticking a team inside must not.
            pop.addEventListener('click', (e) => e.stopPropagation());
            document.addEventListener('click', () => { pop.hidden = true; });
            ms.querySelectorAll('.fe-team-cb').forEach((cb) => {
                cb.addEventListener('change', () => {
                    general.teamIds = Array.from(ms.querySelectorAll('.fe-team-cb'))
                        .filter((x) => x.checked).map((x) => Number(x.value));
                    label.textContent = teamsLabel();
                    markDirty();
                });
            });
        }
    }
    // ── palette ────────────────────────────────────────────────────────────────
    // Its own pane rather than markup stuffed into the step pane, but NOT its own tab: the
    // palette is what a "+ add step" button opens, and it hands the pane straight back to
    // the new step's parameters. `addTarget` is the list that button asked for; without one
    // a node lands after the selected step, or at the end of the flow.
    /** Where the next palette pick lands, in words, for the pane's header. */
    function addTargetLabel() {
        if (addTarget === null) {
            const at = selectedPath ? locate(steps(), selectedPath) : null;
            return at ? t('pf.pal_where_after', { n: String(at.index + 1) }) : t('pf.pal_where_end');
        }
        if (addTarget === '')
            return t('pf.pal_where_end');
        const parts = addTarget.split('/');
        const lane = parts.pop();
        const at = locate(steps(), parts.join('/'));
        return t('pf.pal_where_lane', { lane: t(`pf.lane_${lane}`), n: String((at?.index ?? 0) + 1) });
    }
    /** Insert a fresh node of `op` where addTargetLabel() says, and select it. */
    function addNode(op) {
        const node = newNode(op);
        if (addTarget !== null && addTarget !== '') {
            const parts = addTarget.split('/');
            const lane = parts.pop();
            const owner = nodeAt(steps(), parts.join('/'));
            if (!owner)
                return;
            if (!owner[lane])
                owner[lane] = [];
            owner[lane].push(node);
            selectedPath = `${addTarget}/${owner[lane].length - 1}`;
        }
        else if (addTarget === null && selectedPath) {
            // Straight after the step you are looking at, in whatever list it lives in —
            // appending to the very end would mean scrolling back and reordering.
            const at = locate(steps(), selectedPath);
            if (!at)
                return;
            at.list.splice(at.index + 1, 0, node);
            const parts = selectedPath.split('/');
            parts[parts.length - 1] = String(at.index + 1);
            selectedPath = parts.join('/');
        }
        else {
            steps().push(node);
            selectedPath = String(steps().length - 1);
        }
        addTarget = null;
        markDirty();
        showPane('params'); // land on the new step's parameters, ready to fill in
        render();
    }
    function renderPalette() {
        const groups = Object.fromEntries(CATEGORIES.map((c) => [c, []]));
        for (const [op, spec] of Object.entries(OPS))
            if (offersOp(op))
                groups[spec.category].push(op);
        paletteEl.innerHTML = `
            <div class="fe-pal">
                <p class="settings-hint fe-pal__where">${esc(addTargetLabel())}</p>
                ${categories.map((cat) => `
                    <div class="fe-pal__cat">${esc(t(`pf.cat_${cat}`))}</div>
                    ${groups[cat].map((op) => `<button type="button" class="fe-pal__item" data-op="${esc(op)}">
                        <span class="fe-pal__ico">${OPS[op].icon}</span>${esc(opLabel(op))}</button>`).join('')}
                `).join('')}
            </div>`;
        paletteEl.querySelectorAll('.fe-pal__item').forEach((b) => {
            b.addEventListener('click', () => addNode(b.dataset['op']));
        });
    }
    /** A "+ add step" button: aim the palette at that list and show it. */
    function openPalette(parentPath) {
        addTarget = parentPath;
        showPane('palette');
    }
    // ── events ─────────────────────────────────────────────────────────────────
    // The side pane has no tabs: it is the selected step's parameters, or — while a node is
    // being picked — the palette in their place.
    function showPane(pane) {
        sidePane = pane;
        paramsEl.style.display = pane === 'params' ? '' : 'none';
        paletteEl.style.display = pane === 'palette' ? '' : 'none';
        if (pane === 'palette')
            renderPalette();
    }
    function showTab(next) {
        tab = next;
        root.querySelectorAll('.fe-tab')
            .forEach((x) => x.classList.toggle('active', x.dataset['tab'] === next));
        bodyEl.style.display = next === 'flow' ? '' : 'none';
        // The run bar stays with the log: pausing or stopping a run is exactly what you
        // want to do while reading why it went wrong.
        runEl.style.display = next === 'flow' || next === 'log' ? '' : 'none';
        runLogEl.style.display = next === 'log' ? '' : 'none';
        generalEl.style.display = next === 'general' ? '' : 'none';
        historyEl.style.display = next === 'history' ? '' : 'none';
        infoEl.style.display = next === 'info' ? '' : 'none';
        if (next === 'general')
            renderGeneral();
        else if (next === 'history')
            void renderHistory();
        else if (next === 'info')
            renderInfo();
        else
            requestAnimationFrame(drawArrows); // the canvas was hidden — remeasure
    }
    document.getElementById('back-btn').addEventListener('click', () => {
        if (dirty && !confirm(t('pf.confirm_discard')))
            return;
        navigate({ page: 'flows' });
    });
    root.querySelectorAll('.fe-tab').forEach((b) => {
        b.addEventListener('click', () => showTab(b.dataset['tab']));
    });
    canvasEl.addEventListener('click', (e) => {
        const el = e.target;
        const btn = el.closest('[data-up],[data-down],[data-del],[data-add],[data-after],[data-fold]');
        if (btn) {
            e.stopPropagation();
            // Folding is a view state only — it never touches the document, so no markDirty.
            if (btn.dataset['fold'] !== undefined) {
                const n = nodeAtView(btn.dataset['fold']);
                if (!n)
                    return;
                // On a `run_deposit_flow` / `run_payout_flow` the same control opens the flow
                // it plays, which is read-only and starts closed — the other way round from a
                // lane. Rows inside a played flow fold too, hence the view path.
                const played = playedSteps(n);
                const set = played ? playedOpen : folded;
                if (set.has(n))
                    set.delete(n);
                else
                    set.add(n);
                // A played flow comes from another document, so foldBranches never saw it —
                // fold it once on the first open, or it unrolls whole. Once only: after that
                // the folds inside it are the reader's.
                if (played && !playedFolded.has(n)) {
                    playedFolded.add(n);
                    foldBranches(played);
                }
                byHand.add(n);
                render();
                return;
            }
            const move = (path, delta) => {
                const at = locate(steps(), path);
                if (!at)
                    return;
                const j = at.index + delta;
                if (j < 0 || j >= at.list.length)
                    return;
                const [n] = at.list.splice(at.index, 1);
                at.list.splice(j, 0, n);
                selectedPath = null;
                markDirty();
                render();
            };
            if (btn.dataset['up'] !== undefined)
                return move(btn.dataset['up'], -1);
            if (btn.dataset['down'] !== undefined)
                return move(btn.dataset['down'], 1);
            if (btn.dataset['del'] !== undefined) {
                const at = locate(steps(), btn.dataset['del']);
                if (at) {
                    at.list.splice(at.index, 1);
                    selectedPath = null;
                    markDirty();
                    render();
                }
                return;
            }
            if (btn.dataset['add'] !== undefined) {
                openPalette(btn.dataset['add']);
                return;
            }
            // "+" on the row itself: the palette drops the next step straight after it,
            // wherever in the flow that row is — no scrolling to the end of a lane and
            // dragging the new step back up.
            if (btn.dataset['after'] !== undefined) {
                selectedPath = btn.dataset['after'];
                addTarget = null;
                showPane('palette');
                render();
                return;
            }
        }
        // A row of the played flow is not this document's step — clicking one selects the
        // step that plays it, which IS editable, rather than clearing the selection.
        const step = el.closest('.fe-step:not(.fe-step--ro)');
        if (step) {
            selectedPath = step.dataset['path'] ?? null;
            addTarget = null;
            showPane('params');
            render();
        }
    });
    // A stored run has no phone and nothing to write into one: no inspector, no run bar.
    // The marks are already in `runner` (storedRunHandle) and the document is not editable.
    if (!view)
        mountAuthoring();
    function mountAuthoring() {
        // The phone's screen floats over the page instead of sitting in a side tab: tapping an
        // element there and editing the step it landed in are one motion, and a tab had you
        // flipping between the two. The window closes itself when this page is left.
        const screenWin = createFloatWindow({
            title: t('pf.screen_title'), className: 'fw--screen', anchor: root, resizable: false,
            // Closed = nobody is watching; the live stream inside is dropped (see setVisible).
            onClose: () => inspector?.setVisible(false),
        });
        const openScreen = () => { screenWin.open(); inspector?.setVisible(true); };
        // The inspector writes the tapped element's xpath into the SELECTED step — the whole
        // point of authoring against a live screen instead of typing selectors.
        inspector = mountFlowInspector(screenWin.body, {
            anchor: root,
            toolbar: screenWin.actions,
            onPick: (xpath) => {
                const node = selectedPath ? nodeAt(steps(), selectedPath) : null;
                if (!node) {
                    errEl.textContent = t('pf.err_select_step_first');
                    errEl.style.display = 'block';
                    return;
                }
                errEl.style.display = 'none';
                const spec = OPS[node.op];
                // Write into the locator field this op actually reads — a `keypad` has no
                // `xpath` at all, so tapping a key used to fill a field the engine ignores.
                if (!spec?.fields.includes('xpath') && spec?.fields.includes('key_template')) {
                    node.key_template = keyTemplate(xpath);
                }
                else if (!spec?.fields.includes('xpath') && spec?.fields.includes('until')) {
                    node.until = xpath;
                }
                else {
                    node.xpath = xpath;
                }
                markDirty();
                render();
            },
        });
        // The run bar plays the document as it stands — unsaved edits and all, which is the
        // point: you try a step, fix it, try it again, and only then save. It borrows the
        // inspector's phone rather than picking its own, so authoring against one device and
        // running on another is not a state the editor can get into.
        runner = mountFlowRunPanel(runEl, {
            // As the engine takes it: the kind stamped on, the payout flows its queue steps play
            // embedded (a copy — the document being edited never carries either).
            doc: () => (record.task_type === 'auto'
                ? embedAutoFlows(doc, depositFlows, payoutFlows)
                : embedPayoutFlows(doc, record.task_type, payoutFlows)),
            taskType: record.task_type,
            device: () => inspector.device(),
            pickDevice: () => inspector.pick(),
            openScreen: () => { if (screenWin.isOpen())
                screenWin.close();
            else
                openScreen(); },
            onMarks: () => renderSteps(),
            logHost: runLogEl,
            flowId: record.id,
        });
        // A phone was picked: its screen is being read, so show where it lands.
        inspector.onDevice(() => { runner?.refresh(); openScreen(); });
    }
    // Absent in the run view, which removed it — there is nothing to save there.
    root.querySelector('.fe-save')?.addEventListener('click', async () => {
        errEl.style.display = 'none';
        if (!general.name) {
            errEl.textContent = t('pf.err_name_required');
            errEl.style.display = 'block';
            return;
        }
        const problems = flowProblems(doc, record.task_type);
        if (problems.length && !confirm(t('pf.confirm_save_with_problems')))
            return;
        try {
            // A run opened for editing is a copy that does not exist yet: Save CREATES the
            // flow (never touching the one the run came from, which may since have been
            // edited or deleted) and hands the page over to the real editor of it.
            if (draft) {
                const created = await createPayoutFlow({
                    task_type: record.task_type, app: doc.app, name: general.name,
                    note: general.note, flow: doc,
                });
                if (general.teamIds.length)
                    await setPayoutFlowTeams(created.id, general.teamIds);
                if (general.defaultPick)
                    await updatePayoutFlow(created.id, { default_pick: true });
                dirty = false;
                navigate({ page: 'flows', flowId: created.id });
                return;
            }
            await updatePayoutFlow(record.id, {
                flow: doc, app: doc.app, name: general.name, note: general.note,
                enabled: general.enabled, is_global: false, default_pick: general.defaultPick,
            });
            await setPayoutFlowTeams(record.id, general.teamIds);
            dirty = false;
            savedEl.textContent = t('pf.saved');
            document.getElementById('page-title').textContent = general.name;
        }
        catch (err) {
            errEl.textContent = err.message;
            errEl.style.display = 'block';
        }
    });
    showTab(tab);
    showPane(sidePane);
    render();
}
